import React, { useCallback, useEffect, useRef, useState } from "react";
import { useClient, useMutation } from "jsonapi-react";
import { apiRoutes } from "../../constants";
import DISPLAY_TEXTS from "../../helpers/displayTexts";
import { useToasts } from "react-toast-notifications";
import axios from "axios";
import { AppContext } from "../../context/AppContext";

const BulkActionModal = ({
  userID,
  action,
  type,
  closeBulkModal,
  actionSuccess,
  customClass,
}) => {
  const { currentUser } = React.useContext(AppContext);
  const { addToast } = useToasts();
  const [note, setNote] = useState("");
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const token = localStorage.getItem("access_token");

  const confirmArchiveProfile = async () => {
    if (
      (action === "Ban" && String(note ?? "").trim().length === 0) ||
      (action === "Unban" && String(note ?? "").trim().length === 0)
    ) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setIsLoading(true);

    const apiRequest = async (url, data) => {
      return await axios.post(url, data, {
        headers: {
          Authorization: `JWT ${token}`,
          "Content-Type": "application/json",
        },
      });
    };

    const actions = {
      Profile: () =>
        apiRequest(apiRoutes.bulkAction, {
          data: {
            type: "person",
            attributes: {
              profileIds: userID,
              actionType: action.toLowerCase(),
              ...(action !== "Delete" && note?.trim()
                ? { note: note.trim() }
                : {}),
            },
          },
        }),
      User: () =>
        apiRequest(apiRoutes.userBulkAction, {
          data: {
            type: "accounts",
            attributes: {
              actionType: action.toLowerCase(),
              userIds: userID,
            },
          },
        }),
      Company: () =>
        apiRequest(apiRoutes.companyBulkAction, {
          data: {
            type: "organisation",
            attributes: {
              actionType: "delete",
              companyIds: userID,
            },
          },
        }),
      Contract: () =>
        apiRequest(apiRoutes.contractBulkAction, {
          data: {
            type: "organisation",
            attributes: {
              actionType: "delete",
              contractIds: userID,
            },
          },
        }),
    };

    try {
      const request = actions[type.trim()];
      if (!request) throw new Error("Invalid action type");

      const response = await request();
      setIsLoading(false);
      setNote("");

      addToast(response?.data?.data?.message, { appearance: "success" });
      actionSuccess();
    } catch (error) {
      setIsLoading(false);
      setNote("");
      const errorMessage =
        error.response?.data?.errors[0]?.detail ||
        DISPLAY_TEXTS.SOMETHING_WRONG;
      addToast(errorMessage, { appearance: "error" });
    }
  };

  const DISPLAY_TEXTS_MAP = {
    Delete: {
      User: "Are you sure you want to delete the selected Users?",
      Company: "Are you sure you want to delete the selected Companies?",
      Contract: "Are you sure you want to delete the selected Contracts?",
      Profile: "Are you sure you want to delete the selected Profiles?",
    },
    Activate: {
      User: "Are you sure you want to activate selected Users?",
    },
    Inactivate: {
      User: "Are you sure you want to inactivate selected Users?",
    },
    Archive: {
      Profile: DISPLAY_TEXTS.PERSON_PROFILE_ARCHIVE_WARNING,
    },
    Restore: {
      Profile: DISPLAY_TEXTS.PERSON_PROFILE_RESTORE_WARNING,
    },
    Ban: {
      Profile: DISPLAY_TEXTS.PERSON_PROFILE_BAN_WARNING,
    },
    Unban: {
      Profile: DISPLAY_TEXTS.PERSON_PROFILE_UNBAN_WARNING,
    },
  };

  const WARNING_TEXTS_MAP = {
    Delete: {
      User: DISPLAY_TEXTS.USER_SELF_DELETE_WARNING,
    },
    Inactivate: {
      User: DISPLAY_TEXTS.USER_SELF_INACTIVATE_WARNING,
    },
  };

  const getDisplayText = () => DISPLAY_TEXTS_MAP[action]?.[type] || "";

  const getWarningText = () => WARNING_TEXTS_MAP[action]?.[type] || "";

  const closePopup = () => {
    setNote("");
    closeBulkModal();
  };

  const getActionType = () => {
    return ["Ban", "Unban", "Archive", "Restore"].includes(action);
  };

  return (
    <div
      className={`modal fade show d-block`}
      id="ModalComponent"
      tabIndex="-1"
      role="dialog"
    >
      <div
        className={
          customClass
            ? customClass + " modal-dialog modal-dialog-centered"
            : "modal-dialog modal-dialog-centered"
        }
        role="document"
      >
        <div className="modal-content ">
          <div className="modal-header">
            <h5 className="modal-title">{`${action} ${type}`}</h5>
            <button
              type="button"
              className="close"
              disabled={isLoading}
              onClick={() => closePopup()}
              data-dismiss="modal"
              aria-label="Close"
            >
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {getActionType() ? (
              <div
                className={
                  ["Ban", "Delete", "Unban"].includes(action) &&
                  showError &&
                  !note
                    ? "form-group hasValidationError"
                    : "form-group"
                }
              >
                <div className="warning-text">
                  Note:{"\n"}
                  {getDisplayText()}
                </div>
                <label htmlFor="note">
                  {action === "Ban" || action === "Unban"
                    ? "Reason*"
                    : "Reason"}
                </label>
                <textarea
                  id="archiveNote"
                  type="text"
                  name="note"
                  className="form-control"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                ></textarea>
              </div>
            ) : (
              <div>
                {userID.includes(currentUser?.user_id) &&
                  action !== "Activate" && (
                    <div class="warning-text high-level">
                      <i className="error-icon" />
                      {getWarningText()}
                    </div>
                  )}
                <span>{getDisplayText()}</span>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-primary "
              onClick={() => closePopup()}
              disabled={isLoading}
            >
              {"Close"}
            </button>

            <button
              type="button"
              className={`${
                action === "Delete" || action === "Ban" ? "delete " : ""
              }btn btn-secondary position-relative`}
              onClick={() => confirmArchiveProfile()}
              disabled={isLoading}
            >
              {action}
              {isLoading && (
                <span className="button-loader-container">
                  <div
                    className="spinner-border spinner-border-sm ml-1 text-white"
                    role="status"
                  ></div>
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkActionModal;
